import { HierarchyNode } from 'core/API/hierarchy';
import { getAncestorIds } from 'module/onboarding/utils/hierarchyPermissions';
import { User } from 'types';

const WORKPLACE_HIERARCHY_TYPE = '/api/hierarchy_types/2';

export const getWorkplaceIdFromIri = (iri?: string | null): number | null => {
	if (!iri) return null;
	const parts = String(iri).split('/').filter(Boolean);
	const last = parts[parts.length - 1];
	const num = Number(last);
	return Number.isNaN(num) ? null : num;
};

const getHierarchyTypeChildId = (node: HierarchyNode): string => {
	const typeChild = (node as HierarchyNode & { hierarchyTypeChild?: string | { '@id'?: string } })
		.hierarchyTypeChild;
	if (typeof typeChild === 'string') return typeChild;
	if (typeChild && typeof typeChild === 'object') return typeChild['@id'] ?? '';
	return '';
};

const isWorkplaceHierarchyNode = (node: HierarchyNode): boolean => {
	if (node.type === 'workplace') return true;
	return getHierarchyTypeChildId(node) === WORKPLACE_HIERARCHY_TYPE;
};

export const isUserAssignedToWorkplaceHierarchy = (
	user: User,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	const hierarchyId = user.hierarchy?.id;
	const node = getHierarchyNodeById(hierarchyNodes, hierarchyId);
	if (node) return isWorkplaceHierarchyNode(node);
	return isWorkplaceHierarchyType(user.hierarchy);
};

export const findWorkplaceHierarchyNode = (
	hierarchyNodes: HierarchyNode[],
	workplaceId: number
): HierarchyNode | undefined =>
	hierarchyNodes.find(
		(node) => isWorkplaceHierarchyNode(node) && Number(node.childId) === workplaceId
	);

const getHierarchyNodeById = (
	hierarchyNodes: HierarchyNode[],
	hierarchyId: number | undefined | null
): HierarchyNode | undefined => {
	if (hierarchyId == null) return undefined;
	return hierarchyNodes.find((node) => node.id === hierarchyId);
};

const isWorkplaceHierarchyType = (hierarchy: User['hierarchy']): boolean =>
	hierarchy?.hierarchyTypeChild === WORKPLACE_HIERARCHY_TYPE;

/**
 * Returns the workplace ID a user belongs to when assigned to a workplace hierarchy node.
 */
export const getUserHomeWorkplaceId = (
	user: User,
	hierarchyNodes: HierarchyNode[]
): number | null => {
	const hierarchyId = user.hierarchy?.id;
	const node = getHierarchyNodeById(hierarchyNodes, hierarchyId);

	if (node?.type === 'workplace') {
		const candidate = node.childId ?? node.id;
		const parsed = Number(candidate);
		return Number.isNaN(parsed) ? null : parsed;
	}

	if (isWorkplaceHierarchyType(user.hierarchy) && user.hierarchy?.childId != null) {
		const parsed = Number(user.hierarchy.childId);
		return Number.isNaN(parsed) ? null : parsed;
	}

	return null;
};

export const isUserSharedWithWorkplace = (user: User, workplaceId: number): boolean =>
	getWorkplaceIdFromIri(user.sharedEmployeeWorkplace) === workplaceId;

/**
 * Default workplace user list rule (User Settings without "above workplace" toggle):
 * home workplace matches, or user is shared with the workplace.
 */
export const isUserIncludedInDefaultWorkplaceList = (
	user: User,
	workplaceId: number,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	const homeWorkplaceId = getUserHomeWorkplaceId(user, hierarchyNodes);
	if (homeWorkplaceId === workplaceId) return true;
	return isUserSharedWithWorkplace(user, workplaceId);
};

export interface WorkplaceUserListScopeOptions {
	includeAboveWorkplaceUsers?: boolean;
	currentUserHierarchyId?: number | null;
}

/**
 * Shared workplace user list rule for dropdowns and User Settings.
 * With includeAboveWorkplaceUsers: same branch as current user, excluding other workplaces.
 */
export const isUserIncludedInWorkplaceUserList = (
	user: User,
	workplaceId: number,
	hierarchyNodes: HierarchyNode[],
	{
		includeAboveWorkplaceUsers = false,
		currentUserHierarchyId = null,
	}: WorkplaceUserListScopeOptions = {}
): boolean => {
	if (isUserIncludedInDefaultWorkplaceList(user, workplaceId, hierarchyNodes)) {
		return true;
	}

	if (!includeAboveWorkplaceUsers) {
		return false;
	}

	if (
		!isUserInCurrentUserHierarchySubtree(user, currentUserHierarchyId, hierarchyNodes)
	) {
		return false;
	}

	// Workplace-hierarchy users are only visible for the logged-in workplace (handled above).
	return !isUserAssignedToWorkplaceHierarchy(user, hierarchyNodes);
};

/**
 * True when the user's hierarchy is a custom/organization node above the current workplace.
 */
export const isAboveWorkplaceUser = (
	user: User,
	workplaceHierarchyNodeId: number,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	const userHierarchyId = user.hierarchy?.id;
	if (userHierarchyId == null) return false;

	const ancestors = getAncestorIds(workplaceHierarchyNodeId, hierarchyNodes);
	const aboveNodeIds = ancestors.slice(0, -1);

	return aboveNodeIds.includes(userHierarchyId);
};

export const getDirectChildHierarchyNodeIds = (
	parentHierarchyNodeId: number,
	hierarchyNodes: HierarchyNode[]
): number[] =>
	hierarchyNodes
		.filter((node) => Number(node.parentId) === parentHierarchyNodeId)
		.map((node) => node.id);

/**
 * True when the user is assigned to a hierarchy node that is a direct child of the
 * current user's hierarchy node (exactly one level below).
 */
export const isUserAtDirectChildHierarchy = (
	user: User,
	currentUserHierarchyId: number | null,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	if (currentUserHierarchyId == null) return false;

	const userHierarchyId = user.hierarchy?.id;
	if (userHierarchyId == null) return false;

	const directChildNodeIds = getDirectChildHierarchyNodeIds(
		currentUserHierarchyId,
		hierarchyNodes
	);

	return directChildNodeIds.includes(userHierarchyId);
};

/**
 * True when the user is assigned to the same hierarchy node as the current user.
 */
export const isUserAtSameHierarchy = (
	user: User,
	currentUserHierarchyId: number | null
): boolean => {
	if (currentUserHierarchyId == null) return false;
	return user.hierarchy?.id === currentUserHierarchyId;
};

/**
 * True when the user is assigned to the same hierarchy node as the current user,
 * or to a direct child hierarchy node (exactly one level below).
 */
export const isUserAtSameOrDirectChildHierarchy = (
	user: User,
	currentUserHierarchyId: number | null,
	hierarchyNodes: HierarchyNode[]
): boolean =>
	isUserAtSameHierarchy(user, currentUserHierarchyId) ||
	isUserAtDirectChildHierarchy(user, currentUserHierarchyId, hierarchyNodes);

/**
 * True when the user is assigned to the same hierarchy node as the current user,
 * or to any descendant node in the same branch (direct or indirect children).
 * Excludes users in parallel/unrelated branches and users above the current user.
 */
export const isUserInCurrentUserHierarchySubtree = (
	user: User,
	currentUserHierarchyId: number | null,
	hierarchyNodes: HierarchyNode[]
): boolean => {
	if (currentUserHierarchyId == null) return false;

	const userHierarchyId = user.hierarchy?.id;
	if (userHierarchyId == null) return false;

	const userAncestors = getAncestorIds(userHierarchyId, hierarchyNodes);
	return userAncestors.includes(currentUserHierarchyId);
};

export interface FilterUserSettingsUsersOptions {
	workplaceId: number;
	hierarchyNodes: HierarchyNode[];
	showAboveWorkplaceUsers: boolean;
	currentUserHierarchyId?: number | null;
}

/**
 * Filters users for the User Settings list based on the currently selected workplace.
 *
 * Default: current workplace users + users shared with the current workplace.
 * Optional: include users at the same hierarchy as the current user or any descendant
 * custom/organization hierarchy node in the same branch (excluding other workplaces).
 */
export const filterUserSettingsUsers = (
	users: User[],
	{
		workplaceId,
		hierarchyNodes,
		showAboveWorkplaceUsers,
		currentUserHierarchyId = null,
	}: FilterUserSettingsUsersOptions
): User[] => {
	return users.filter((user) =>
		isUserIncludedInWorkplaceUserList(user, workplaceId, hierarchyNodes, {
			includeAboveWorkplaceUsers: showAboveWorkplaceUsers,
			currentUserHierarchyId,
		})
	);
};
